/* Core & libraries */
import React, { useEffect, useState } from "react";
import { useTheme } from "react-jss";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap"

/* Helpers, constants, custom methods, internal libraries */
import { PEOPLE_TEAMS_ROUTE, TRAVELER_DETAILS_ROUTE } from "../../../../constants/routes";
import { useUserService } from "../../../../services/userService";
import { useLocaleContext } from "../../../../contexts/localeContext.js";
import { AIRLINES } from "../../../../constants/appConstants";

/* Components */
import UCGrid from "../../../ucgrid";
import UCInput from "../../../ucinput/index.js";
import DeleteActionModal from "../../../modals/deleteActionModal";
import { ReactComponent as UserDeleteIcon } from "../../../../resources/images/user-x.svg";
import { ReactComponent as UpdateTeamIcon } from "../../../../resources/images/people.svg";
import { ReactComponent as CCImage } from "../../../../resources/images/cc.svg";
import UpdateTeamActionModal from "../../../modals/updateTeamActionModal";
import WarningModal from "../../../modals/warningModal";
import { dateFormatWithLocale } from '../../../../helpers/general';
import PeopleNoRecords from "../../../peopleNoRecords";

/* Styles */
import styles from "./styles.js"

const messages = defineMessages({
  removePlaceholder: {
    id: 'admin.remove.lastATM.removePlaceholder'
  },
})

const gridSpinnerName = "peopleGridSpinner"

const PeopleGrid = (props) => {
  const { gridOptions, setGridOptions, peopleResponse, deletePeople, updatePeople, gridActionPermision } = props;
  const theme = useTheme();
  const classes = styles({ theme });
  const navigate = useNavigate();
  const { userDetails } = useUserService();
  const intl = useIntl();

  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [removeInput, setRemoveInput] = useState("");
  const [showDeleteLastAtmModal, setShowDeleteLastAtmModal] = useState(false);
  const [showUpdateTeamModal, setShowUpdateTeamModal] = useState(false);
  const [showRemoveCitiOnlyModal, setShowRemoveCitiOnlyModal] = useState(false);
  const [permissionsResponse, setGridPermissionsResponse] = useState({});

  const globalLocale = useLocaleContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  useEffect(() => {
    if(!showDeleteLastAtmModal) {
      setRemoveInput("")
    }
  }, [showDeleteLastAtmModal])

  const getDeleteContent = () => (
    <>
      <div className={classes.deleteActionDescription}><FormattedMessage id="admin.remove.lastATM.description" /></div>
      <div className={classes.deleteActionNote}><FormattedMessage id="admin.remove.lastATM.note" /></div>
      <UCInput
        inputClassName={classes.removeInput}
        placeholder={intl.formatMessage(messages.removePlaceholder)}
        onChange={event => setRemoveInput(event.target.value)}
      />
    </>
  )


  const disableRemove = () => {
    const word = intl.formatMessage(messages.removePlaceholder);
    return removeInput !== word;
  }

  const actionCallback = async (actionType, row) => {
    const response = await gridActionPermision(row.entityID, `${gridSpinnerName}.${actionType}Action`)
    if (response) {
      setGridPermissionsResponse(response)
      switch (actionType) {
        case "delete":
          if (userDetails?.manageCompany && response.lastAdmin) {
            setShowDeleteLastAtmModal(true)
          } else if (response.canRemove) {
            setShowDeleteModal(true)
          } else {
            setShowRemoveCitiOnlyModal(true)
          }
          break;
        case "edit":
          response.canChangeRole ?
            setShowUpdateTeamModal(true) :
            setShowRemoveCitiOnlyModal(true)
          break;
      }
    }
    return false;
  }

  const deleteAction = async () => {
    const response = await deletePeople(selectedRow)
    if (response) {
      setShowDeleteModal(false)
    }
  }

  const deleteLastATMAction = async () => {
    const response = await deletePeople(selectedRow)
    if (response) {
      setShowDeleteLastAtmModal(false)
    }
  }

  const updateAction = async () => {
    const values = {
      userId: selectedRow.entityID,
      firstName:selectedRow.firstName,
      lastName: selectedRow.lastName,
      roleName: selectedRow.roleName,
      roleId: selectedOption
    }

    const response = await updatePeople(values)
    if (response) {
      setShowUpdateTeamModal(false)
    }
  }

  return (
    <div className={classes.peopleGrid}>
      <UCGrid
        gridName="peopleGrid"
        data={ peopleResponse?.results }
        columns={ peopleResponse?.columns }
        actions={ peopleResponse?.actions }
        handlePageChange={(page) => {setGridOptions({
          ...gridOptions,
          page
        })
        page > 1 ? navigate(`/app/people?page=${page}`) : navigate(`/app/people`)
        }}
        setSelectedRow={setSelectedRow}
        actionCallback={actionCallback}
        spinner={gridSpinnerName}
        customColumns={{
          parentName: (row) => (
            <Link to={`${"../../" + PEOPLE_TEAMS_ROUTE}/${row.parentID}`} className={classes.link}>
              {row.parentName}
            </Link>
          ),
          roleName: (row) => (
            <p className={classes.role}>
              <Link to={`${row.entityID}`} className={classes.link}>
                <FormattedMessage id={`roles.${row.roleId}.${providerCode}`} />
              </Link>
              {
                row.authorizedUser && providerCode === AIRLINES.AA &&
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="tooltip" className={classes.tooltip}>
                      {
                        row.primaryContact ? 
                          <FormattedMessage id="peopleGrid.primarycitiicon"/> 
                        : <FormattedMessage id="peopleGrid.citiicon"/>
                      }
                    </Tooltip>
                  }
                >
                  <CCImage className={classes.cc}/>
                </OverlayTrigger>
              }
            </p>
          ),
          createdDateTime: (row) => (
            dateFormatWithLocale(row.createdDateTime, globalLocale)
          ),
        }}
        columnCustomWidth={{
          avatarName: "280px !important",
          roleName: "195px !important"
        }}
        disableDeleteBy={"entityID"}
        disableDeleteByValue={userDetails?.userInfo?.userEntityId}
        noDataComponent={ <PeopleNoRecords /> }
        defaultSort={peopleResponse?.defaultSort}
        handleSort={(column, sortDirection) =>
          setGridOptions({
            ...gridOptions,
            sortby: column.id,
            sortdirection: sortDirection === "desc" ? "dsc" : sortDirection,
          })
        }
        onRowClickRoute={row => TRAVELER_DETAILS_ROUTE.replace(":travelerId", row.entityID)}
      />

      {
        peopleResponse?.actions && peopleResponse.actions.map((action, i) => {
          switch (action) {
            case "delete":
              return <React.Fragment key={`${action}-${i}`}>
                <DeleteActionModal
                  showModal={showDeleteModal}
                  setShowModal={setShowDeleteModal}
                  setSelectedRow={setSelectedRow}
                  modalIcon={ <UserDeleteIcon /> }
                  modalTitle={ <FormattedMessage id="peopleGrid.deleteAction.title" values={{name: `${selectedRow?.firstName} ${selectedRow?.lastName}`}}/> }
                  modalDescription={ <FormattedMessage id="peopleGrid.deleteAction.description" /> }
                  submitCallback={deleteAction}
                />

                <DeleteActionModal
                  showModal={showDeleteLastAtmModal}
                  setShowModal={ setShowDeleteLastAtmModal }
                  modalIcon={ <UserDeleteIcon /> }
                  modalTitle={ <FormattedMessage id="admin.remove.lastATM.title" values={{name: `${selectedRow?.firstName} ${selectedRow?.lastName}`}}/> }
                  modalDescription={ getDeleteContent() }
                  submitCallback={ deleteLastATMAction }
                  setSelectedRow={(value) => console.log(value)}
                  disableSubmit={ disableRemove() }
                  submitText={ <FormattedMessage id="peopleGrid.deleteAction.remove"/> }
                />
            </React.Fragment>
            case "edit":
            return <UpdateTeamActionModal
                showModal={showUpdateTeamModal}
                setShowModal={setShowUpdateTeamModal}
                setSelectedRow={setSelectedRow}
                selectedRow={selectedRow}
                role={selectedRow?.roleId}
                setSelectedOption={setSelectedOption}
                modalIcon={ <UpdateTeamIcon /> }
                modalTitle={ <FormattedMessage id="peopleGrid.editAction" />  }
                modalDescription={ <FormattedMessage id="peopleGrid.updateTeamAction.description" values={{name: `${selectedRow?.firstName} ${selectedRow?.lastName}`}}/> }
                submitCallback={updateAction}
                key={`${action}-${i}`}
              />
          }
        })
      }
      <WarningModal
        showModal={showRemoveCitiOnlyModal}
        setShowModal={setShowRemoveCitiOnlyModal}
        modalIcon={ <UserDeleteIcon /> }
        modalTitle={ <FormattedMessage id="peopleGrid.citiUserWarning.title"/> }
        modalDescription={ permissionsResponse.isCiti ? <FormattedMessage id="peopleGrid.citiUserWarning.description" /> : <FormattedMessage id="peopleGrid.citiUserWarning.notCiti.description" />}
      />
    </div>
  )
}

export default PeopleGrid;
